import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Bar,
  BarChart,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import { CHART_GROW_ANIMATION } from '../chartAnimation';
import { ClrBarHover } from '../chartMarkHover';
import ClrRechartsTooltip from '../ClrRechartsTooltip';
import { buildNiceAxisScale, getSeriesMax } from '../axisScale';
import {
  categoryTickLabel,
  isCategoryPadPayload,
  padCategoriesToBottom,
} from '../padCategoryAxis';

const BAR_COLOR = '#26735C';
const FIRST_GRID_COLOR = '#54555A';
const GRID_COLOR = '#DBDEE4';
const BAR_SIZE = 24.040231704711914;
const MIN_CATEGORY_SLOTS = 5;

function DenialReasonsCard({ data }) {
  const chartData = useMemo(() => (data || [])
    .map((r) => ({
      reason: r.reason || r.denial_reason || 'Not specified',
      count: r.count ?? r.denial_count ?? 0,
    }))
    .filter((r) => r.count > 0)
    .sort((a, b) => b.count - a.count), [data]);

  const plotData = useMemo(
    () => padCategoriesToBottom(chartData, {
      minSlots: MIN_CATEGORY_SLOTS,
      categoryKey: 'reason',
      valueKeys: ['count'],
    }),
    [chartData],
  );

  const axis = useMemo(
    () => buildNiceAxisScale(getSeriesMax(chartData, ['count'])),
    [chartData],
  );

  const dashedTicks = useMemo(
    () => (axis.ticks || []).filter((tick) => tick > 0),
    [axis.ticks],
  );

  const yAxisWidth = useMemo(() => {
    const longest = chartData.reduce(
      (max, row) => Math.max(max, String(row.reason || '').length),
      0,
    );
    // ~6.5px per character at 11px; keep a tight left gutter without clipping long reasons
    return Math.min(118, Math.max(64, Math.ceil(longest * 6.5) + 10));
  }, [chartData]);

  return (
    <div className="clr-chart-card clr-denial-card">
      <div className="clr-chart-card-header clr-chart-card-header-accent">
        <h3 className="clr-chart-card-title">Denial analysis</h3>
      </div>
      <div className="clr-chart-card-body clr-denial-body">
        {chartData.length === 0 ? (
          <p className="clr-chart-plot clr-chart-empty">No denial data</p>
        ) : (
          <div className="clr-chart-plot">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                layout="vertical"
                data={plotData}
                margin={{ top: 4, right: 8, left: 0, bottom: 0 }}
              >
                <ReferenceLine x={0} stroke={FIRST_GRID_COLOR} strokeWidth={1} zIndex={100} />
                {dashedTicks.map((tick) => (
                  <ReferenceLine
                    key={`grid-${tick}`}
                    x={tick}
                    stroke={GRID_COLOR}
                    strokeWidth={1}
                    strokeDasharray="2 2"
                    zIndex={100}
                  />
                ))}
                <XAxis
                  type="number"
                  domain={[0, axis.domainMax]}
                  ticks={axis.ticks}
                  height={24}
                  tickMargin={4}
                  tick={{ fontSize: 11, fill: '#6b7a74' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  type="category"
                  dataKey="reason"
                  width={yAxisWidth}
                  tick={{ fontSize: 11, fill: '#3e4f4c' }}
                  tickMargin={4}
                  tickFormatter={categoryTickLabel}
                  axisLine={false}
                  tickLine={false}
                />
                <ClrRechartsTooltip
                  cursor={{ fill: 'rgba(62, 79, 76, 0.04)' }}
                  formatter={(payload) => {
                    if (isCategoryPadPayload(payload)) return { title: null, rows: [] };
                    const row = payload[0]?.payload;
                    return {
                      title: row?.reason || '',
                      rows: [{
                        key: 'count',
                        label: 'Denials',
                        value: row?.count ?? payload[0]?.value ?? 0,
                      }],
                    };
                  }}
                />
                <Bar
                  dataKey="count"
                  fill={BAR_COLOR}
                  activeBar={ClrBarHover}
                  radius={[0, 0, 0, 0]}
                  barSize={BAR_SIZE}
                  maxBarSize={BAR_SIZE}
                  {...CHART_GROW_ANIMATION}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}

DenialReasonsCard.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({
    reason: PropTypes.string,
    denial_reason: PropTypes.string,
    count: PropTypes.number,
    denial_count: PropTypes.number,
  })),
};

export default DenialReasonsCard;
